import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faCheck, faPlus, faGear } from '@fortawesome/free-solid-svg-icons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import './BotSelector.css';

// Compact bot switcher pinned to the top of the sidebar, directly under
// the workspace switcher. Selection-only: clicking a bot makes it the
// globally active bot that every section page reads. Creating / renaming
// / deleting bots lives on the "Manage bots" page (/my-bots).
const BotSelector = ({ variant } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const { bots, selectedBot, selectedBotId, setSelectedBotId, openAddBot, loading } =
    useSelectedBot();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSelect = (id) => {
    if (String(id) === String(selectedBotId)) {
      setOpen(false);
      return;
    }
    requestUnsavedAction(() => {
      setSelectedBotId(id);
      setOpen(false);
    });
  };

  const handleManage = () => {
    setOpen(false);
    requestUnsavedAction(() => navigate('/my-bots'));
  };

  const handleAdd = () => {
    setOpen(false);
    openAddBot();
  };

  // Both the label and the chevron just toggle the dropdown — the
  // trigger is selection-only and never navigates anywhere.
  const toggleOpen = () => setOpen((v) => !v);

  const currentName =
    selectedBot?.name ||
    (loading ? t('bot_selector_loading') : t('bot_selector_no_agents'));

  return (
    <div className={`bot-selector${variant === "header" ? " header" : ""}`} ref={ref}>
      <div className={`bot-selector-trigger${open ? ' open' : ''}`}>
        <button
          type="button"
          className="bot-selector-label"
          onClick={toggleOpen}
          title={currentName}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="bot-selector-icon">
            <FontAwesomeIcon icon={faComment} />
          </span>
          <span className="bot-selector-text">
            <span className="bot-selector-role">{t('bot_selector_current_agent')}</span>
            <span className="bot-selector-name">{currentName}</span>
          </span>
        </button>
        <button
          type="button"
          className="bot-selector-chev-btn"
          onClick={toggleOpen}
          title={t('bot_selector_switch_agent')}
          aria-label={t('bot_selector_switch_agent')}
        >
          <ExpandMoreIcon
            className="bot-selector-chev"
            sx={{
              transition: 'transform 0.25s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </div>

      {open && (
        <div className="bot-selector-menu" role="listbox">
          <div className="bot-selector-menu-header">{t('bot_selector_agents_header')}</div>

          {loading && bots.length === 0 && (
            <div className="bot-selector-empty">{t('bot_selector_loading')}</div>
          )}

          {!loading && bots.length === 0 && (
            <div className="bot-selector-empty">{t('bot_selector_no_agents')}</div>
          )}

          {bots.map((b) => {
            const isCurrent = String(b.id) === String(selectedBotId);
            return (
              <button
                key={b.id}
                type="button"
                className={`bot-selector-item ${isCurrent ? 'current' : ''}`}
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSelect(b.id)}
              >
                <span className="bot-selector-item-check">
                  {isCurrent && <FontAwesomeIcon icon={faCheck} />}
                </span>
                <span className="bot-selector-item-text">
                  <span className="bot-selector-item-name">{b.name}</span>
                </span>
              </button>
            );
          })}

          <div className="bot-selector-divider" />

          <button
            type="button"
            className="bot-selector-item action"
            onClick={handleAdd}
          >
            <span className="bot-selector-item-check">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <span className="bot-selector-item-text">
              <span className="bot-selector-item-name">{t('bot_selector_new_agent')}</span>
            </span>
          </button>

          <button
            type="button"
            className="bot-selector-item action"
            onClick={handleManage}
          >
            <span className="bot-selector-item-check">
              <FontAwesomeIcon icon={faGear} />
            </span>
            <span className="bot-selector-item-text">
              <span className="bot-selector-item-name">{t('bot_selector_manage_agents')}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default BotSelector;
